import { Box, Link, Paper, Stack, Typography } from "@mui/material";
import GoHomeButton from "./GoHomeButton";

const LAST_UPDATED = "September 5, 2026";

export default function PrivacyPolicy({ onNavigateHome, onNavigate = () => {}, homeLabel = "Go Home" }) {
  return (
    <Paper elevation={2} className="page-card" sx={{ p: 3, mt: 3, mb: 4 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5" className="section-header">Privacy Policy</Typography>
        <GoHomeButton onNavigateHome={onNavigateHome} label={homeLabel} />
      </Stack>

      <Stack spacing={1.5}>
        <Typography variant="body2" color="text.secondary">Last updated: {LAST_UPDATED}</Typography>

        <Typography variant="body2" color="text.secondary">
          This Privacy Policy explains how LostNSound Enterprises ("LostNSound," "we," "us," or "our")
          collects, uses, and shares information when you use Wishlist Manager (the "Service") at
          wishlistmgr.com. By using the Service, you agree to this Policy.
        </Typography>

        <Typography variant="h6" sx={{ mt: 2 }}>1. Information We Collect.</Typography>

        <Typography variant="subtitle2" sx={{ mt: 1 }}>Information you provide:</Typography>
        <Box component="ul" sx={{ pl: 3, m: 0 }}>
          <li>
            <Typography variant="body2" component="span" color="text.secondary">
              Account information: the display name (your first and last name) and the private user
              ID you choose when you register; and your email address and password, or, if you sign
              in with Google, the basic account information Google shares with us (your email address
              and name). Your email address and password are held by our authentication provider
              (Supabase); we never store your password ourselves.
            </Typography>
          </li>
          <li>
            <Typography variant="body2" component="span" color="text.secondary">
              Profile settings: whether you identify yourself as a parent/guardian/caregiver who
              manages lists for others, your primary Group, and display preferences such as font size
              and sort order.
            </Typography>
          </li>
          <li>
            <Typography variant="body2" component="span" color="text.secondary">
              Wishlist content: the list and item information you enter — list names, item
              descriptions, links, prices, notes, priority order, and any photos you upload.
            </Typography>
          </li>
          <li>
            <Typography variant="body2" component="span" color="text.secondary">
              Information about people you add: if you add a list for someone who cannot manage their
              own — such as a child, grandchild, or elderly parent — we collect the name you enter
              for that person, the relationship label you select, and the gift information you add on
              their behalf. See Section 7.
            </Typography>
          </li>
          <li>
            <Typography variant="body2" component="span" color="text.secondary">
              Group information: the Groups you create or join, your membership, join/leave/leader
              requests, and Group Leader messages (a free-text note that Group Leaders often use to
              share an event date, time, and location).
            </Typography>
          </li>
          <li>
            <Typography variant="body2" component="span" color="text.secondary">
              Claiming activity: which items you mark as claimed or purchased on other members'
              lists. This is deliberately hidden from the list owner — it is the core privacy feature
              of the Service.
            </Typography>
          </li>
          <li>
            <Typography variant="body2" component="span" color="text.secondary">
              Invitations you send: if you use the bulk invite feature, the email addresses you enter
              for people who are not yet users, so we can send them an invitation.
            </Typography>
          </li>
          <li>
            <Typography variant="body2" component="span" color="text.secondary">
              Messages and feedback: anything you send through the in-app messaging or Send Feedback
              features.
            </Typography>
          </li>
        </Box>

        <Typography variant="subtitle2" sx={{ mt: 1 }}>Information collected automatically:</Typography>
        <Box component="ul" sx={{ pl: 3, m: 0 }}>
          <li>
            <Typography variant="body2" component="span" color="text.secondary">
              Diagnostic logs: to operate and secure the Service, we record events such as sign-ins,
              list and item changes, claims, and errors, together with your user ID, a timestamp, and
              technical context. A small number of these log entries (for example, a password-reset
              request or a duplicate sign-up attempt) include the email address involved.
            </Typography>
          </li>
          <li>
            <Typography variant="body2" component="span" color="text.secondary">
              Technical and connection data: our hosting and infrastructure providers (Cloudflare and
              Supabase) automatically process your IP address, device and browser type, and request
              metadata, and our authentication provider records sign-in events, as a normal part of
              delivering and protecting any website.
            </Typography>
          </li>
          <li>
            <Typography variant="body2" component="span" color="text.secondary">
              Local storage: after you sign in, the Service stores your session token in your
              browser's local storage so you stay logged in. This is strictly necessary for the
              Service to work. We do not use advertising or analytics cookies, and we do not use any
              third-party analytics, advertising, or behavioral-tracking services.
            </Typography>
          </li>
        </Box>

        <Typography variant="h6" sx={{ mt: 2 }}>2. How We Use Information.</Typography>
        <Typography variant="body2" color="text.secondary">
          We use the information above to: create and operate your account, wishlists, Groups, and
          the claiming feature; send you transactional email — account verification, password resets,
          and Group invitations — through our email provider (Resend); keep the Service secure,
          enforce access controls that limit data to the right account and Group, prevent abuse, and
          debug problems; respond to your messages, feedback, and support requests; and comply with
          legal obligations and enforce our Terms of Service. We do not use your information for
          advertising, and we do not sell it.
        </Typography>

        <Typography variant="h6" sx={{ mt: 2 }}>3. How We Share Information.</Typography>
        <Typography variant="body2" color="text.secondary">
          With other members: your list name and items are visible to members of your Group. Your
          claiming activity is visible to other members but hidden from the owner of the list you
          claimed from. Group Leaders can see Group membership and manage Group settings.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          With service providers who process data only to provide services to us:
        </Typography>
        <Box component="ul" sx={{ pl: 3, m: 0 }}>
          <li>
            <Typography variant="body2" component="span" color="text.secondary">
              Supabase — database, authentication, and file storage. Data is hosted in the United
              States.
            </Typography>
          </li>
          <li>
            <Typography variant="body2" component="span" color="text.secondary">
              Cloudflare — website hosting and content delivery.
            </Typography>
          </li>
          <li>
            <Typography variant="body2" component="span" color="text.secondary">
              Resend — sending transactional email.
            </Typography>
          </li>
          <li>
            <Typography variant="body2" component="span" color="text.secondary">
              Google — only if you choose "Continue with Google"; Google's Privacy Policy governs the
              information you provide to Google.
            </Typography>
          </li>
          <li>
            <Typography variant="body2" component="span" color="text.secondary">
              Amazon — only if you use the Amazon import feature. We fetch the public wishlist page at
              the URL you provide and read its item details. We send Amazon only that request; we do
              not send Amazon any information about you. We currently have no affiliate relationship
              with Amazon.
            </Typography>
          </li>
        </Box>
        <Typography variant="body2" color="text.secondary">
          For legal reasons: we may disclose information if required by law or valid legal process,
          or to protect the rights, safety, or property of LostNSound, our users, or the public.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          In a business transfer: if the Service is transferred to a new operator (including a
          company formed to run it) or is involved in a merger, acquisition, or sale of assets, your
          information may be transferred as part of that transaction; any recipient will remain bound
          by this Policy or a policy at least as protective.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          We do not sell your personal information, and we do not share it for cross-context
          behavioral advertising.
        </Typography>

        <Typography variant="h6" sx={{ mt: 2 }}>4. Data Retention.</Typography>
        <Typography variant="body2" color="text.secondary">
          We keep your account information and content for as long as your account is active. When
          you delete your account (from the Settings screen), we delete your profile, your wishlists
          (including any lists you manage for others), your wishlist items (including any photos
          attached to them), your Group memberships, and your authentication account. Items you
          claimed on other members' lists are unclaimed but not deleted, because they belong to
          those members. Feedback you submitted is kept but disassociated from your account.
          Diagnostic logs are retained for a limited period for security and troubleshooting and are
          then purged (we target a maximum of 90 days). Residual copies may persist in encrypted
          backups for a limited time before they expire.
        </Typography>

        <Typography variant="h6" sx={{ mt: 2 }}>5. Your Choices and Rights.</Typography>
        <Typography variant="body2" color="text.secondary">
          You can review and update most of your information directly in the Service — edit your
          profile in Settings, edit or delete list items at any time, and delete your entire account
          from the Settings screen. Depending on where you live, you may have additional rights under
          laws such as the EU/UK GDPR or U.S. state privacy laws (including in California, Colorado,
          Connecticut, Utah, and Virginia), such as the right to access, correct, delete, or receive
          a copy of your personal information, or to appeal a decision we make about a request. To
          make such a request, contact us at privacy@wishlistmgr.com. We will not discriminate
          against you for exercising these rights. If you are in the EEA or UK, our legal bases for
          processing are performance of our contract with you (to provide the Service), our
          legitimate interests (to secure and improve the Service), your consent (where requested),
          and compliance with legal obligations.
        </Typography>

        <Typography variant="h6" sx={{ mt: 2 }}>6. Security.</Typography>
        <Typography variant="body2" color="text.secondary">
          We protect your information using measures that include encryption in transit, database
          row-level security policies that restrict data to the appropriate account and Group, and
          the security controls of our infrastructure providers. No method of transmission or storage
          is completely secure, and we cannot guarantee absolute security. If you believe your
          account has been compromised, contact us at privacy@wishlistmgr.com.
        </Typography>

        <Typography variant="h6" sx={{ mt: 2 }}>7. Children's Privacy.</Typography>
        <Typography variant="body2" color="text.secondary">
          The Service is intended for adults and is not directed to children. We do not allow anyone
          under 18 to create an account, and we do not knowingly collect personal information
          directly from children. A parent, legal guardian, or caregiver may choose to add a list for
          a child they are responsible for. In that case, the only information about the child is
          what the adult account holder enters — typically the child's first name, a relationship
          label, and gift ideas. The child has no account, no login, and no way to interact with the
          Service, and we do not collect any information from the child directly. An adult who adds
          such a list is responsible for the information they provide and for any consent required
          within their family. If you are a parent or guardian and believe a child has provided us
          personal information directly, or you want information about a child removed, contact us at
          privacy@wishlistmgr.com and we will delete it.
        </Typography>

        <Typography variant="h6" sx={{ mt: 2 }}>8. International Users.</Typography>
        <Typography variant="body2" color="text.secondary">
          The Service is operated in the United States and our providers store data in the United
          States. If you access the Service from outside the United States, you understand that your
          information will be transferred to, stored, and processed in the United States, where
          data-protection laws may differ from those in your country.
        </Typography>

        <Typography variant="h6" sx={{ mt: 2 }}>9. Changes to This Policy.</Typography>
        <Typography variant="body2" color="text.secondary">
          We may update this Policy from time to time. If we make a material change, we will provide
          notice through the Service or by email before it takes effect and update the "last updated"
          date above. Your continued use of the Service after the change means you accept the updated
          Policy.
        </Typography>

        <Typography variant="h6" sx={{ mt: 2 }}>10. Contact.</Typography>
        <Typography variant="body2" color="text.secondary">
          Questions, concerns, or privacy requests: privacy@wishlistmgr.com, or use the Send Feedback
          box on the Messages screen.
        </Typography>

        <Typography variant="body2" sx={{ mt: 2 }}>
          See also:{" "}
          <Link component="button" variant="body2" onClick={() => onNavigate("terms")}>
            Terms of Service
          </Link>
        </Typography>
      </Stack>
    </Paper>
  );
}
